import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { api, money } from "../lib/api";
import { useLang } from "../context/LangContext";

const VER = "1.14.0";
const loadMoyasar = () =>
  new Promise((resolve, reject) => {
    if (window.Moyasar) return resolve();
    const css = document.createElement("link");
    css.rel = "stylesheet";
    css.href = `https://cdn.moyasar.com/mpf/${VER}/moyasar.css`;
    document.head.appendChild(css);
    const s = document.createElement("script");
    s.src = `https://cdn.moyasar.com/mpf/${VER}/moyasar.js`;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("load"));
    document.head.appendChild(s);
  });

export default function PayPage() {
  const { number } = useParams();
  const [sp] = useSearchParams();
  const token = sp.get("token");
  const paymentId = sp.get("id");
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [order, setOrder] = useState(null);
  const [state, setState] = useState(paymentId ? "verifying" : "form");
  const [err, setErr] = useState("");
  const mounted = useRef(false);
  const done = `/order/${number}?token=${token}`;

  useEffect(() => {
    api.get(`/orders/${number}`, { params: { token } }).then((r) => setOrder(r.data)).catch(() => setState("error"));
  }, [number, token]);

  useEffect(() => {
    if (!paymentId) return;
    api.post("/payments/confirm", { order_number: number, payment_id: paymentId })
      .then((r) => {
        if (r.data.paid) nav(done, { replace: true });
        else { setErr(sp.get("message") || r.data.message || t("pay_failed")); setState("form"); nav(`/pay/${number}?token=${token}`, { replace: true }); }
      })
      .catch(() => { setErr(t("pay_failed")); setState("form"); });
  }, [paymentId]); // eslint-disable-line

  useEffect(() => {
    if (!order || state !== "form" || paymentId) return;
    if (order.status !== "pending_payment") { nav(done, { replace: true }); return; }
    if (mounted.current) return;
    mounted.current = true;
    (async () => {
      const { data: cfg } = await api.get("/payments/config");
      if (!cfg.enabled) return setErr(t("pay_failed"));
      await loadMoyasar();
      window.Moyasar.init({
        element: ".mysr-form",
        amount: Math.round(order.total * 100),
        currency: "SAR",
        description: `QAMAR order ${order.order_number}`,
        publishable_api_key: cfg.publishable_key,
        callback_url: `${window.location.origin}/pay/${number}?token=${token}`,
        metadata: { order_number: order.order_number },
        methods: ["creditcard"],
        supported_networks: ["mada", "visa", "mastercard"],
        language: lang,
      });
    })().catch(() => setErr(t("pay_failed")));
  }, [order, state]); // eslint-disable-line

  if (state === "error") return <p className="container-q py-32 text-center font-display text-2xl">{t("not_found")}</p>;

  return (
    <div data-testid="pay-page" className="container-q max-w-xl pb-28 pt-14">
      <p className="eyebrow mb-3">{t("online_pay")}</p>
      <h1 className="text-4xl">{t("pay_title")}</h1>
      {order && <p data-testid="pay-amount" className="mt-3 text-lg">{order.order_number} · {money(order.total)} {t("sar")}</p>}
      {state === "verifying" ? (
        <p className="mt-10 flex items-center gap-3" data-testid="pay-verifying"><Loader2 className="animate-spin" size={18} />{t("verifying")}</p>
      ) : (
        <>
          {err && <p data-testid="pay-error" className="mt-6 bg-red-50 p-3 text-sm text-red-700">{err}</p>}
          <div className="mysr-form mt-8" dir="ltr" data-testid="moyasar-form" />
          <p className="mt-6 flex items-center gap-2 text-xs text-ink/60"><ShieldCheck size={14} />{t("secure_note")}</p>
        </>
      )}
    </div>
  );
}
